import { useAssetUrl } from '../../common/Helpers';
import { Label } from '../label/Label';
import Styles from './Video.module.css';
import { Telemetry } from '@shared/frontend';
import { RefObject, useMemo } from 'react';
import type { IAssetDTO } from '../../models/IAssetDTO';

/** Props for the Video component. */
interface IVideoDTO {
  videoFallback: string;
  loadingText: string;
  videoUrl?: string;
  assetDTO?: IAssetDTO;
  rtl?: boolean;
  classNameContainer?: string;
  classNameVideo?: string;
  classNameLoading?: string;
  ref?: RefObject<HTMLDivElement | null>;
  // Stable telemetry surface key. When set, the control emits a `media_played` event on every `play` (reliable across browsers, unlike click-on-native-controls).
  telemetryKey?: string;
}

/**
 * Renders a Video control. Plays either a directly-supplied URL or a video asset resolved from a manifest and bundle.
 *
 * @param props - Component props.
 * @returns The rendered Video element.
 */
export function Video(props: IVideoDTO) {
  const { videoFallback, loadingText, videoUrl, assetDTO, rtl, classNameContainer, classNameVideo, classNameLoading, ref, telemetryKey } = props;
  const assetURL = useAssetUrl(assetDTO ?? null);
  // A directly-supplied URL takes precedence over a resolved asset (mirrors how Image prefers spriteUrl over assetDTO).
  const videoURL = videoUrl ?? assetURL;

  // Compute class names an inline styles
  const containerClassName = useMemo(() => (classNameContainer ? `${Styles.container} ${classNameContainer}` : Styles.container), [classNameContainer]);

  // Prefix textual content with the right-to-left marker when rtl so mixed-direction strings render correctly (mirrors Image's resolvedTitle).
  const resolvedFallback = useMemo(() => (rtl && videoFallback ? `\u200F${videoFallback}` : videoFallback), [rtl, videoFallback]);
  const resolvedLoadingText = useMemo(() => (rtl && loadingText ? `\u200F${loadingText}` : loadingText), [rtl, loadingText]);

  // Render Video element
  return (
    <>
      {videoURL ? (
        <div ref={ref} className={containerClassName}>
          <video className={classNameVideo} src={videoURL} controls preload="metadata" onPlay={telemetryKey ? () => Telemetry.MediaPlayed(Telemetry.MediaTypes.Video, telemetryKey) : undefined}>
            <Label text={resolvedFallback} />
          </video>
        </div>
      ) : (
        <div className={classNameLoading}>{resolvedLoadingText}</div>
      )}
    </>
  );
}
